import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

import {chemicalBaseTypeNames, countries, systemTypeNamesFormat} from 'helpers/constants';
import {systemOptionsTags} from 'helpers/dataHelpers';
import getProductDict from '../system/productDict';

import {Col, Row} from 'components';
import {Radio} from 'components/forms';
import {Select, Input, Checkbox} from 'components/forms/readOnly';
import {FormattedMessage, injectIntl, intlShape} from 'react-intl';

let iconStyle = {marginLeft: 5, marginRight: 5, marginTop: 1, color: '#fff'};

function propertyOptions(pool, prop, formatMessage) {
  return Object.values(systemOptionsTags[pool.typeID][prop]).map(x => formatMessage({id: x}));
}

function propertyKeys(pool, prop) {
  return Object.keys(systemOptionsTags[pool.typeID][prop]);
}

const FullSystemCard = ({customer, pool, products, dealer, onChange, selected, intl}) => {
  const {formatMessage, formatNumber} = intl;

  let flagged = pool.flag ? <i className='bl bl-flag-filled pull-right'
                               style={iconStyle}/> : null;
  let sourceConcern = customer && customer.sourceConcern ? <i className='bl bl-water-pipe pull-right'
                                                              style={iconStyle}/> : null;

  let prevAnalysisDate = pool.prevAnalysisDate ? moment(pool.prevAnalysisDate).format('ll') : null;

  let {productDict, pool: p} = getProductDict({pool, products, dealer, formatMessage});

  const systemTypeNames = systemTypeNamesFormat(formatMessage);


  return <div className={classNames('select-master-card', {'select-master-card--outlined': selected})}
              onClick={onChange}>
    <div className='select-master-card__header'>
      <Radio checked={selected}
             onChange={onChange}/>
      <h3 className='select-master-card__title'>
        {pool.softSoakTRIO ? 'Trio ' : null}
        {chemicalBaseTypeNames(pool.typeID, pool.chemicalBaseTypeID || 2, formatMessage)}
      </h3>
      <p className='select-master-card__last-analysis'>
        <span className='select-master-card__last-analysis-label'><FormattedMessage id='general.last.analysis'/>:&nbsp;</span>
        {prevAnalysisDate || <FormattedMessage id='general.none' />}
      </p>
      {sourceConcern}
      {flagged}
    </div>
    <div className='select-master-card__content'>
      {/* NOTE: using read only select fields here because these will probably be made editable in the future and we need to calculate the information
               anyway to show the product name */}
      <h4 className='select-master-card__sub-heading'><FormattedMessage id='general.system.details'/></h4>
      <Row>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.system.type'})}
                  value={p.typeID}
                  options={Object.values(systemTypeNames)}
                  keys={Object.keys(systemTypeNames).map(x => parseInt(x))}/>
        </Col>
        <Col md={6}>
          <Input label={formatMessage({id: 'general.volume'})}
                 value={formatNumber(p.volume) + (dealer.country === countries.CA ? ' L' : ' gals.')}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.location'})}
                  value={p.location}
                  options={propertyOptions(p, 'location', formatMessage)}
                  keys={propertyKeys(p, 'location')}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.finish'})}
                  value={p.finish}
                  options={propertyOptions(p, 'finish', formatMessage)}
                  keys={propertyKeys(p, 'finish')}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.ground.level'})}
                  value={p.groundLevel}
                  options={propertyOptions(p, 'groundLevel', formatMessage)}
                  keys={propertyKeys(p, 'groundLevel')}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.installation.type'})}
                  value={p.property}
                  options={propertyOptions(p, 'property', formatMessage)}
                  keys={propertyKeys(p, 'property')}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.filter.media'})}
                  value={p.filterMedia}
                  options={propertyOptions(p, 'filterMedia', formatMessage)}
                  keys={propertyKeys(p, 'filterMedia')}/>
        </Col>
        <Col md={6}>
          <Checkbox label={formatMessage({id: 'system.details.vacuum.waste'})}
                    value={p.cannotVacToWaste}/>
        </Col>
        <Col md={12}>
          <Checkbox label={formatMessage({id: 'system.details.installed.by'}, {name: dealer.name})}
                    value={p.installedByDealer}/>
        </Col>
      </Row>

      <h4 className='select-master-card__sub-heading'><FormattedMessage id='trublue.product.preferences'/></h4>
      <Row>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.sanitizer'})}
                  value={p.sanitizer}
                  options={productDict.sanitizer.names}
                  keys={productDict.sanitizer.keys}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.maint.oxidizer'})}
                  value={p.maintOxidizer}
                  options={productDict.maintOxidizer.names}
                  keys={productDict.maintOxidizer.keys}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.prev.algicide'})}
                  value={p.prevAlgicide}
                  options={productDict.prevAlgicide.names}
                  keys={productDict.prevAlgicide.keys}/>
        </Col>
        <Col md={6}/>

        <Col md={6}>
          <Select label={formatMessage({id: 'customer.product.profile.troubleshooting'})}
                  value={p.oxidizer}
                  options={productDict.oxidizer.names}
                  keys={productDict.oxidizer.keys}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.maint.clarifier'})}
                  value={p.maintClarifier}
                  options={productDict.maintClarifier.names}
                  keys={productDict.maintClarifier.keys}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'customer.product.profile.phadjuster'})}
                  value={p.pHAdjuster}
                  options={productDict.pHAdjuster.names}
                  keys={productDict.pHAdjuster.keys}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'general.ph.buffer'})}
                  value={p.pHBuffer}
                  options={productDict.pHBuffer.names}
                  keys={productDict.pHBuffer.keys}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'customer.product.profile.winterizer'})}
                  value={p.winterOxidizer}
                  options={productDict.winterOxidizer.names}
                  keys={productDict.winterOxidizer.keys}/>
        </Col>
        <Col md={6}>
          <Select label={formatMessage({id: 'chemicals.winter.algicide'})}
                  value={p.winterAlgicide}
                  options={productDict.winterAlgicide.names}
                  keys={productDict.winterAlgicide.keys}/>
        </Col>
      </Row>
    </div>
  </div>;
};

FullSystemCard.propTypes = {
  customer: PropTypes.object.isRequired,
  dealer: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  pool: PropTypes.object.isRequired,
  products: PropTypes.array.isRequired,
  selected: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(FullSystemCard);
